// As consultas ficam num lugar só para a chave de cache ser única por dado.
// Chave repetida com formato diferente em duas telas é o jeito de ter o mesmo
// projeto em dois estados ao mesmo tempo na mesma página.
import { useQuery } from '@tanstack/react-query';
import { pegar } from './cliente';
import type {
  BuscaRecente, Commit, DecisaoCompleta, DiffDaDoc, Doc, Documento, Impacto, Job, Lacuna, Nota, Overview,
  Projeto, ProjetoAdmin, Proposta, PropostaAberta, RespostaDeBusca, Saude, Sessao, SessaoAberta,
  Usuario, VersaoDaDoc,
} from './tipos';
import type { Dono, GrafoBruto } from '../grafo/nucleo';

export const chaves = {
  projetos: ['projetos'] as const,
  jobs: ['jobs'] as const,
  usuarios: ['usuarios'] as const,
  overview: (slug: string) => ['projeto', slug, 'overview'] as const,
  docs: (slug: string) => ['projeto', slug, 'docs'] as const,
  doc: (slug: string, chave: string) => ['projeto', slug, 'doc', chave] as const,
  historico: (slug: string, chave: string) => ['projeto', slug, 'doc-historico', chave] as const,
  diff: (slug: string, chave: string, para: number) => ['projeto', slug, 'doc-diff', chave, para] as const,
  grafo: (slug: string) => ['projeto', slug, 'grafo'] as const,
  memoria: (slug: string) => ['projeto', slug, 'memoria'] as const,
  notas: (slug: string, chave?: string | null) => ['projeto', slug, 'notas', chave || '__todas__'] as const,
  decisoes: (slug: string) => ['projeto', slug, 'decisoes'] as const,
  donos: (slug: string) => ['projeto', slug, 'donos'] as const,
  propostas: (slug: string) => ['projeto', slug, 'propostas'] as const,
  proposta: (slug: string, id: string) => ['projeto', slug, 'proposta', id] as const,
  sessoes: (slug: string) => ['projeto', slug, 'sessoes'] as const,
  sessao: (slug: string, id: string) => ['projeto', slug, 'sessao', id] as const,
  tour: (slug: string) => ['projeto', slug, 'tour'] as const,
  saude: (slug: string) => ['projeto', slug, 'saude'] as const,
  lacunas: (slug: string) => ['projeto', slug, 'lacunas'] as const,
  commits: (slug: string) => ['projeto', slug, 'commits'] as const,
  busca: (slug: string, q: string) => ['projeto', slug, 'busca', q] as const,
  buscas: (slug: string) => ['projeto', slug, 'buscas'] as const,
  impacto: (slug: string, chave: string, prof: number) => ['projeto', slug, 'impacto', chave, prof] as const,
};

export const useProjetos = () => useQuery({
  queryKey: chaves.projetos,
  queryFn: () => pegar<Projeto[]>('/api/projects'),
});

export const useOverview = (slug: string) => useQuery({
  queryKey: chaves.overview(slug),
  queryFn: () => pegar<Overview>(`/api/${slug}/overview`),
});

export const useDocs = (slug: string) => useQuery({
  queryKey: chaves.docs(slug),
  queryFn: () => pegar<Documento[]>(`/api/${slug}/docs`),
});

export const useDoc = (slug: string, chave: string) => useQuery({
  queryKey: chaves.doc(slug, chave),
  queryFn: () => pegar<Doc>(`/api/${slug}/doc?key=${encodeURIComponent(chave)}`),
  enabled: !!chave,
});

export const useHistoricoDaDoc = (slug: string, chave: string) => useQuery({
  queryKey: chaves.historico(slug, chave),
  queryFn: () => pegar<{ versions?: VersaoDaDoc[] }>(`/api/${slug}/doc/history?key=${encodeURIComponent(chave)}`),
  select: (r) => r.versions || [],
  enabled: !!chave,
});

export const useDiffDaDoc = (slug: string, chave: string, para: number | null) => useQuery({
  queryKey: chaves.diff(slug, chave, para || 0),
  queryFn: () => pegar<DiffDaDoc>(`/api/${slug}/doc/diff?key=${encodeURIComponent(chave)}&to=${para}`),
  enabled: !!chave && !!para,
});

export const useGrafoDoCodigo = (slug: string) => useQuery({
  queryKey: chaves.grafo(slug),
  queryFn: () => pegar<GrafoBruto>(`/api/${slug}/graph`),
});

/**
 * O grafo da memória só é buscado quando alguém pede um modo que o use.
 *
 * É a mesma economia do painel atual (graph.js:62): trocar de modo não custa
 * requisição nova, mas quem nunca sai do modo código também nunca paga por ela —
 * e ela é a mais cara do projeto, porque cruza decisão, nota, sessão e lacuna.
 */
export const useGrafoDaMemoria = (slug: string, ativo: boolean) => useQuery({
  queryKey: chaves.memoria(slug),
  queryFn: () => pegar<GrafoBruto>(`/api/${slug}/memory-graph`),
  enabled: ativo,
});

export interface RespostaNotas { notes: Nota[]; counts: Record<string, number> }
const buscarNotas = (slug: string, chave?: string | null) => pegar<RespostaNotas>(
  `/api/${slug}/notes${chave ? `?key=${encodeURIComponent(chave)}` : ''}`,
);

/** Notas de uma unidade (ou de todas, quando a chave é nula). */
export const useNotas = (slug: string, chave?: string | null) => useQuery({
  queryKey: chaves.notas(slug, chave),
  queryFn: () => buscarNotas(slug, chave),
});

export const useContagemDeNotas = (slug: string) => useQuery({
  queryKey: chaves.notas(slug, null),
  queryFn: () => buscarNotas(slug),
  select: (r) => r.counts || {},
});

export type Decisao = DecisaoCompleta;
export const useDecisoes = (slug: string) => useQuery({
  queryKey: chaves.decisoes(slug),
  queryFn: () => pegar<{ decisions: DecisaoCompleta[] }>(`/api/${slug}/decisions`),
  select: (r) => r.decisions || [],
});

export const useDonos = (slug: string) => useQuery({
  queryKey: chaves.donos(slug),
  queryFn: () => pegar<{ owners: Dono[] }>(`/api/${slug}/owners`),
  select: (r) => r.owners || [],
});

export const usePropostas = (slug: string) => useQuery({
  queryKey: chaves.propostas(slug),
  queryFn: () => pegar<{ proposals: Proposta[]; pending: number }>(`/api/${slug}/proposals?status=all`),
});

/**
 * O miolo de uma proposta só é buscado quando alguém abre o cartão.
 *
 * É a mesma economia de knowledge.js:196-207: a lista traz vinte cartões, e cada
 * corpo carrega o texto proposto, o texto do acervo e o diff linha a linha —
 * buscar os vinte de uma vez para ler um seria pagar a fila inteira por cartão.
 */
export const usePropostaAberta = (slug: string, id: string | null) => useQuery({
  queryKey: chaves.proposta(slug, id || ''),
  queryFn: () => pegar<PropostaAberta>(`/api/${slug}/proposals/${encodeURIComponent(id || '')}`),
  enabled: !!id,
});

export const useSessoes = (slug: string) => useQuery({
  queryKey: chaves.sessoes(slug),
  queryFn: () => pegar<{ sessions: Sessao[]; unconfirmed: { id: string }[] }>(
    `/api/${slug}/sessions?status=all&limit=100`,
  ),
});

/** Diário e resumo de uma sessão, só quando o cartão dela abre. */
export const useSessaoAberta = (slug: string, id: string | null) => useQuery({
  queryKey: chaves.sessao(slug, id || ''),
  queryFn: () => pegar<SessaoAberta>(`/api/${slug}/sessions/${encodeURIComponent(id || '')}`),
  enabled: !!id,
});

export const useSaude = (slug: string) => useQuery({
  queryKey: chaves.saude(slug),
  queryFn: () => pegar<Saude>(`/api/${slug}/health`),
});

export const useLacunas = (slug: string) => useQuery({
  queryKey: chaves.lacunas(slug),
  queryFn: () => pegar<{ gaps: Lacuna[]; detected: Lacuna[] }>(`/api/${slug}/gaps?status=open`),
});

export const useCommits = (slug: string) => useQuery({
  queryKey: chaves.commits(slug),
  queryFn: () => pegar<Commit[]>(`/api/${slug}/commits`),
});

export const useBusca = (slug: string, q: string) => useQuery({
  queryKey: chaves.busca(slug, q),
  queryFn: () => pegar<RespostaDeBusca>(`/api/${slug}/search?q=${encodeURIComponent(q)}`),
  enabled: !!q,
  // busca custa LLM: repetir a mesma pergunta em cinco minutos reusa a resposta,
  // que é o mesmo cache que o servidor mantém em search_history
  staleTime: 5 * 60_000,
});

export const useBuscasRecentes = (slug: string) => useQuery({
  queryKey: chaves.buscas(slug),
  queryFn: () => pegar<BuscaRecente[]>(`/api/${slug}/searches`),
});

export const useImpacto = (slug: string, chave: string, profundidade: number) => useQuery({
  queryKey: chaves.impacto(slug, chave, profundidade),
  queryFn: () => pegar<Impacto>(
    `/api/${slug}/impact?key=${encodeURIComponent(chave)}&depth=${profundidade}`,
  ),
  enabled: !!chave,
});

/** Progresso do tour por usuário: passos vistos e a trilha escolhida. */
export const useTour = (slug: string, ativo: boolean) => useQuery({
  queryKey: chaves.tour(slug),
  queryFn: () => pegar<{ done?: string[]; persona?: string | null }>(`/api/${slug}/tour`),
  enabled: ativo,
});

export const useProjetosAdmin = () => useQuery({
  queryKey: chaves.projetos,
  queryFn: () => pegar<ProjetoAdmin[]>('/api/projects'),
});

export const useJobs = () => useQuery({
  queryKey: chaves.jobs,
  queryFn: () => pegar<Job[]>('/api/jobs'),
  // job andando muda de estado sozinho; o painel atual repetia a chamada a cada
  // 2,5 s enquanto havia fila (app.js:811)
  refetchInterval: (consulta) => {
    const lista = consulta.state.data;
    return Array.isArray(lista) && lista.some((j) => j.status === 'queued' || j.status === 'running')
      ? 2500
      : false;
  },
});

export const useUsuarios = () => useQuery({
  queryKey: chaves.usuarios,
  queryFn: () => pegar<Usuario[]>('/api/users'),
});
